// Third Party
import * as React from 'react';
import cx from 'classnames';
import { twMerge } from '@utils/tw-merge';
import { WithChildrenCommonProps } from '@types';
import {
  getLinkButtonStylesAsClassName,
  getLinkStylesAsClassName,
} from './link-styles';
import { ButtonColor, ButtonSize, ButtonVariant } from '@components/button';

// allow this component to accept all properties of "a" tag
export interface IAnchorExtendedProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string | { href: string; as: string };
  prefetch?: boolean;
  /** Applies the colors for dark mode over the default */
  isDark?: boolean;
  /** Applies the button styles */
  isButton?: boolean;
  /** If the button will take up the full width of its container */
  isFullWidth?: boolean;
  /** Color of the button */
  color?: ButtonColor;
  /** Sets disabled state and applies disabled styles. It can be clicked */
  isDisabled?: boolean;
  /** Size of the button */
  size?: ButtonSize;
  /** The variant theme for the button */
  variant?: ButtonVariant;
  /** Sets button to loading state */
  isLoading?: boolean;
  /** Icon to display on the right side of the link text (when isButton) */
  rightIcon?: React.ReactNode;
}

export type ILinkProps = WithChildrenCommonProps<IAnchorExtendedProps>;

/**
 * Application Link Component
 *
 * Usage:
 *
 * ```
 * <Link to="/about">
 *   About
 * </Link>
 * ```
 *
 * or
 *
 * ```
 * <Link to={ href: "/products/[id]" as: "/product/1" }>
 *   Product 1
 * </Link>
 * ```
 *
 */
export const Link = React.forwardRef(
  (
    {
      isLoading = false,
      isFullWidth = false,
      isDisabled = false,
      color = 'default',
      isButton = false,
      size = 'md',
      variant = 'filled',
      to,
      prefetch,
      dataId,
      isDark,
      rightIcon,
      children,
      className: classNameProp,
      ...props
    }: ILinkProps,
    ref: React.ForwardedRef<HTMLAnchorElement>
  ) => {
    const computedClassName = isButton
      ? getLinkButtonStylesAsClassName(
          isFullWidth,
          color,
          size,
          variant,
          false,
          isDisabled
        )
      : getLinkStylesAsClassName(isDark, color);
    const tailwindClassName = twMerge(computedClassName, classNameProp);

    // Render content with optional icon (only when isButton)
    const renderContent = () => {
      if (isButton && rightIcon) {
        return (
          <>
            {children}
            <span
              className={cx('inline-flex shrink-0', { 'ml-2': !!children })}
            >
              {rightIcon}
            </span>
          </>
        );
      }
      return children;
    };

    if (typeof to === 'string') {
      return (
        <a
          href={to}
          className={tailwindClassName}
          data-id={dataId}
          {...props}
          ref={ref}
        >
          {renderContent()}
        </a>
      );
    } else {
      return (
        <a
          href={to.href}
          className={tailwindClassName}
          data-id={dataId}
          {...props}
          ref={ref}
        >
          {renderContent()}
        </a>
      );
    }
  }
);

Link.displayName = 'Link';
